import React, { useEffect } from 'react';
import { GraphQlRequestService } from '../../api/graphql-request-service';
import { DefaultPermissionService } from '../../api/permission-service';
import { DefaultUserApiService, UserApiService } from '../../api/user-service';
import { BaseAuthedTracker } from '../analytics/trackers/baseTracker';
import {
  OriginalRoleViewAuthorizationService,
  OriginalRoleViewAuthorizationServiceImpl,
} from '../api/authorization/service';
import { BackendFieldsService } from '../api/fields/service';
import { useFrontendStartup } from '../api/frontendStartup/hooks';
import { FrontendStartupService } from '../api/frontendStartup/service';
import { Hasher } from '../api/hasher';
import { AssignedPermissions, Permission } from '../api/permission/types';
import { BackendRoleService } from '../api/role/service';
import { hashString, RoleId } from '../api/types';
import { AuthedErrorTracker } from '../common/components/errors/trackers/AuthedErrorTracker';
import {
  AllLatestDomainSettingsContext,
  AllowedDomainsContext,
  AuthedErrorTrackerContext,
  BackendRoleServiceContext,
  BaseAuthedTrackerContext,
  CurrentUserContext,
  EffectiveRoleContext,
  FieldsServiceContext,
  FrontendStartupContext,
  FrontendStartupWithSimulation,
  GraphQlRequestServiceContext,
  LegacyStoresContext,
  NullableDomainContext,
  OriginalRoleViewAuthorizationServiceContext,
  useAuthUserContext,
  useBaseTrackerContext,
  useDomainFromLocalStorageContext,
  useEnvironmentServiceContext,
  useIntercomStoreContext,
  useLanguageStoreContext,
  useLocalStorageCurrentUserServiceContext,
  useLocalStorageDomainServiceContext,
  useLocalStorageExecutorRoleServiceContext,
  UserRoleIdContext,
  useSimulatedRoleContext,
  useUserTrackerContext,
} from './contexts';
import { useMemoWithDeps } from './hooks';
import { TokenLoader } from './TokenLoader';

const DebugPrefix = 'AuthenticatedContextProvider';

const FrontedStartupLoader = ({
  children,
  token,
}: {
  children: (
    frontendStartupData: FrontendStartupWithSimulation,
    graphQlRequestService: GraphQlRequestService,
    permissionService: DefaultPermissionService,
    errorTracker: AuthedErrorTracker
  ) => React.ReactNode;
  token: string;
}) => {
  const authUser = useAuthUserContext();
  const environmentService = useEnvironmentServiceContext();
  const localStorageExecutorRoleService =
    useLocalStorageExecutorRoleServiceContext();
  const simulatedRole = useSimulatedRoleContext();
  const localStorageDomainService = useLocalStorageDomainServiceContext();
  const localStorageCurrentUserService =
    useLocalStorageCurrentUserServiceContext();
  const simulatedRoleHash = Hasher.hash(simulatedRole);

  const errorTracker = useMemoWithDeps(
    `${DebugPrefix} - errorTracker`,
    () =>
      new AuthedErrorTracker(
        environmentService,
        authUser,
        localStorageCurrentUserService,
        localStorageDomainService,
        localStorageExecutorRoleService
      ),
    [authUser.uid, simulatedRoleHash],
    [
      environmentService,
      localStorageCurrentUserService,
      localStorageDomainService,
      localStorageExecutorRoleService,
    ]
  );

  const graphQlRequestServiceDepsHash = hashString([
    ...errorTracker.getDeps(),
    ...environmentService.getDeps(),
  ]);

  // Keep useMemoWithDeps invalidation behavior (nested service deps) without
  // logging sensitive token values through debug dependency output.
  const graphQlRequestService = React.useMemo(
    () => new GraphQlRequestService(errorTracker, token, environmentService),
    [token, graphQlRequestServiceDepsHash]
  );

  const permissionService = useMemoWithDeps(
    `${DebugPrefix} - permissionService`,
    () =>
      new DefaultPermissionService(
        graphQlRequestService,
        simulatedRole?.id ?? null
      ),
    [simulatedRoleHash],
    [graphQlRequestService]
  );

  const frontendStartupService = useMemoWithDeps(
    `${DebugPrefix} - frontendStartupService`,
    () => new FrontendStartupService(graphQlRequestService),
    [],
    [graphQlRequestService]
  );

  const { data: frontendStartup, error } = useFrontendStartup(
    `${DebugPrefix} - frontendStartup`,
    frontendStartupService,
    simulatedRole
  );

  if (error || !frontendStartup) {
    throw new Error(
      'Frontend Startup failed to load: ' +
        (error ? error.message : 'No data returned')
    );
  }

  return (
    <>
      {children(
        frontendStartup,
        graphQlRequestService,
        permissionService,
        errorTracker
      )}
    </>
  );
};

export const AuthenticatedContextProviderInner = ({
  children,
  frontendStartup,
  graphQlRequestService,
  permissionService,
  errorTracker,
}: {
  children: React.ReactNode;
  frontendStartup: FrontendStartupWithSimulation;
  graphQlRequestService: GraphQlRequestService;
  permissionService: DefaultPermissionService;
  errorTracker: AuthedErrorTracker;
}) => {
  console.log2('AuthenticatedContextProvider');
  const baseTracker = useBaseTrackerContext();
  const userTracker = useUserTrackerContext();
  const languageStore = useLanguageStoreContext();
  const intercomStore = useIntercomStoreContext();
  const simulatedRole = useSimulatedRoleContext();
  const domainFromLocalStorage = useDomainFromLocalStorageContext();
  const simulatedRoleHash = Hasher.hash(simulatedRole); // We can just use the ID because role ids are unique

  // frontendStartup only changes when simulatedRole changes, so we use simulatedRoleHash
  // instead of hashing large data objects (performance optimization - ENG-8794)
  const currentUser = useMemoWithDeps(
    `${DebugPrefix} - currentUser from frontendStartup`,
    () => frontendStartup.currentUser,
    [simulatedRoleHash]
  );
  useEffect(() => {
    if (!currentUser) {
      throw new Error('Frontend Startup current user is undefined');
    }
    userTracker.trackAppLogin(currentUser.email);
  }, [currentUser, userTracker]);

  if (!currentUser) {
    throw new Error('Frontend Startup current user is undefined');
  }

  const userRole: RoleId = simulatedRole?.id ?? currentUser.roleId;

  const allowedDomains = useMemoWithDeps(
    `${DebugPrefix} - allowedDomains`,
    () => frontendStartup.roleDetails?.allowedDomains ?? [],
    [simulatedRoleHash]
  );

  const allLatestDomainSettings = useMemoWithDeps(
    `${DebugPrefix} - all latestDomainSettings from allowedDomains`,
    () =>
      allowedDomains.flatMap((domainInfo) =>
        domainInfo.latestSettings ? [domainInfo.latestSettings] : []
      ),
    [simulatedRoleHash]
  );

  const userApiService: UserApiService = useMemoWithDeps(
    `${DebugPrefix} - userApiService`,
    () => new DefaultUserApiService(graphQlRequestService),
    [],
    [graphQlRequestService]
  );

  const legacyStores = useMemoWithDeps(
    `${DebugPrefix} - legacyStores`,
    () => ({
      intercomStore,
      languageStore,
      userApiService,
      permissionService,
    }),
    [],
    [intercomStore, languageStore, userApiService, permissionService]
  );
  // END LEGACY STORES

  const domain = useMemoWithDeps(
    `${DebugPrefix} - domain from allowedDomains`,
    () =>
      allowedDomains.find((d) => d.domain === domainFromLocalStorage)?.domain ??
      null,
    [simulatedRoleHash, domainFromLocalStorage ?? undefined]
  );

  const baseAuthedTracker = useMemoWithDeps(
    `${DebugPrefix} - baseAuthedTracker`,
    () => new BaseAuthedTracker(baseTracker, { email: currentUser.email }),
    [simulatedRoleHash],
    [baseTracker]
  );

  const backendRoleService: BackendRoleService = useMemoWithDeps(
    `${DebugPrefix} - backendRoleService`,
    () =>
      new BackendRoleService(
        graphQlRequestService,
        currentUser.roleId,
        simulatedRole
      ),
    [simulatedRoleHash],
    [graphQlRequestService]
  );

  const effectiveRoleId = useMemoWithDeps(
    `${DebugPrefix} - effectiveRoleId`,
    () => backendRoleService.getEffectiveRoleId(),
    [],
    [backendRoleService]
  );

  const fieldsService = useMemoWithDeps(
    `${DebugPrefix} - fieldsService`,
    () => new BackendFieldsService(graphQlRequestService, simulatedRole),
    [simulatedRoleHash],
    [graphQlRequestService]
  );

  const userRoleAllPermissions: AssignedPermissions<Permission> =
    frontendStartup.originalRoleDetails.allPermissions;
  if (!userRoleAllPermissions) {
    throw new Error(
      `All permissions for current user from frontend startup is undefined`
    );
  }

  const originalRoleViewAuthorizationService: OriginalRoleViewAuthorizationService =
    useMemoWithDeps(
      `${DebugPrefix} - OriginalRoleViewAuthorizationService`,
      () =>
        new OriginalRoleViewAuthorizationServiceImpl(userRoleAllPermissions),
      [simulatedRoleHash]
    );

  return (
    <OriginalRoleViewAuthorizationServiceContext.Provider
      value={originalRoleViewAuthorizationService}
    >
      <FrontendStartupContext.Provider value={frontendStartup}>
        <BackendRoleServiceContext.Provider value={backendRoleService}>
          <EffectiveRoleContext.Provider value={effectiveRoleId}>
            <AuthedErrorTrackerContext.Provider value={errorTracker}>
              <GraphQlRequestServiceContext.Provider
                value={graphQlRequestService}
              >
                <AllowedDomainsContext.Provider value={allowedDomains}>
                  <NullableDomainContext.Provider value={domain}>
                    <UserRoleIdContext.Provider value={userRole}>
                      <AllLatestDomainSettingsContext.Provider
                        value={allLatestDomainSettings}
                      >
                        <CurrentUserContext.Provider value={currentUser}>
                          <LegacyStoresContext.Provider value={legacyStores}>
                            <BaseAuthedTrackerContext.Provider
                              value={baseAuthedTracker}
                            >
                              <FieldsServiceContext.Provider
                                value={fieldsService}
                              >
                                {children}
                              </FieldsServiceContext.Provider>
                            </BaseAuthedTrackerContext.Provider>
                          </LegacyStoresContext.Provider>
                        </CurrentUserContext.Provider>
                      </AllLatestDomainSettingsContext.Provider>
                    </UserRoleIdContext.Provider>
                  </NullableDomainContext.Provider>
                </AllowedDomainsContext.Provider>
              </GraphQlRequestServiceContext.Provider>
            </AuthedErrorTrackerContext.Provider>
          </EffectiveRoleContext.Provider>
        </BackendRoleServiceContext.Provider>
      </FrontendStartupContext.Provider>
    </OriginalRoleViewAuthorizationServiceContext.Provider>
  );
};

export const AuthenticatedContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <TokenLoader debugString={`${DebugPrefix} - token`}>
      {(token) => (
        <FrontedStartupLoader token={token}>
          {(
            frontendStartupData,
            graphQlRequestService,
            permissionService,
            errorTracker
          ) => (
            <AuthenticatedContextProviderInner
              frontendStartup={frontendStartupData}
              graphQlRequestService={graphQlRequestService}
              permissionService={permissionService}
              errorTracker={errorTracker}
            >
              {children}
            </AuthenticatedContextProviderInner>
          )}
        </FrontedStartupLoader>
      )}
    </TokenLoader>
  );
};
